import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useCampaigns } from "@/hooks/useCampaigns";
import { Skeleton } from "@/components/ui/skeleton";
import { optimizeImageUrl } from "@/lib/utils";
import { VerifiedBadge } from "@/components/VerifiedBadge";

export const FeaturedFundraisers = () => {
  const navigate = useNavigate();
  const { campaigns, loading } = useCampaigns();

  // Filter for active campaigns and take first 6
  const activeCampaigns = campaigns
    .filter((campaign) => campaign.status === 'active')
    .slice(0, 6);

  const calculateProgress = (spent: number | null, budget: number | null) => {
    if (!budget || budget === 0) return 0;
    return Math.min(((spent || 0) / budget) * 100, 100);
  };

  return (
    <section className="py-8 sm:py-12 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl">
        <div className="text-center mb-8 sm:mb-12 space-y-4">
          <div className="inline-flex items-center gap-3 mx-auto">
            <span className="h-px w-8 bg-primary/60" />
            <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-primary">
              Live right now
            </span>
            <span className="h-px w-8 bg-primary/60" />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.1] text-foreground">
            Fundraisers{" "}
            <span className="font-serif italic font-medium text-primary">worth</span>{" "}
            backing today.
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto">
            Real campaigns, raising real money — pick one and move it forward.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="h-40 sm:h-48 w-full" />
                <CardContent className="p-4 sm:p-5">
                  <Skeleton className="h-5 sm:h-6 w-3/4 mb-3 sm:mb-4" />
                  <Skeleton className="h-2 w-full mb-2" />
                  <Skeleton className="h-5 sm:h-6 w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {activeCampaigns.map((campaign) => (
              <Card
                key={campaign.id}
                className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => navigate(`/campaign/${campaign.id}`)}
              >
                <div className="relative h-40 sm:h-48 overflow-hidden">
                  <img
                    src={optimizeImageUrl(campaign.image_url, 400)}
                    alt={campaign.name}
                    width="400"
                    height="300"
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {campaign.location && (
                    <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 bg-black/70 text-white px-2 sm:px-3 py-1 rounded text-xs sm:text-sm font-medium">
                      {campaign.location}
                    </div>
                  )}
                </div>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-center gap-1.5 mb-2">
                    <h3 className="font-semibold text-sm sm:text-base text-foreground line-clamp-2 flex-1">
                      {campaign.name}
                    </h3>
                    <VerifiedBadge 
                      isVerified={campaign.is_fully_verified} 
                      fundraiserType={campaign.fundraiser_type}
                      size="md"
                    />
                  </div>
                  {campaign.fundraiser_name && (
                    <p className="text-xs text-muted-foreground mb-2 sm:mb-3">
                      by {campaign.fundraiser_company || campaign.fundraiser_name}
                    </p>
                  )}
                  <div className="space-y-2">
                    <Progress 
                      value={calculateProgress(campaign.spent, campaign.budget)} 
                      className="h-2" 
                      aria-label={`Fundraising progress: ${Math.round(calculateProgress(campaign.spent, campaign.budget))}% of goal reached`}
                    />
                    <p className="text-sm sm:text-base text-foreground font-bold">
                      £{(campaign.spent || 0).toLocaleString()} raised
                      {campaign.budget && ` of £${campaign.budget.toLocaleString()}`}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="text-center mt-6 sm:mt-10">
          <Button 
            variant="outline" 
            size="lg" 
            className="px-6 sm:px-8 w-full sm:w-auto"
            onClick={() => navigate('/explore')}
          >
            Explore All Fundraisers
          </Button>
        </div>
      </div>
    </section>
  );
};
